/**
 * Confirmation dialog for text and screenshot references captured from a PDF.
 * @module medicalNecessityAnalysis/components/ReviewPdfProofModal
 */
import React, { useEffect, useState } from "react";
import AlertCircleIcon from "../../../svg/alert-circle-icon";
import { PdfProofPreview } from "../types/pdfProof";
import {
  GENERIC_COMMENT_WARNING_MESSAGE,
  isGenericComment,
} from "../utils/genericCommentValidation";

/** Props for {@link ReviewPdfProofModal}. */
interface ReviewPdfProofModalProps {
  /** Text or image shown above the comment field. */
  preview: PdfProofPreview;
  /** Comment to prefill when an existing reference is reopened. */
  initialComment: string;
  /** Whether the dialog is editing an already-saved reference. */
  isEditing: boolean;
  /** Whether a screenshot is currently being uploaded. */
  isSaving: boolean;
  /** Closes the dialog and discards any unsaved draft. */
  onCancel: () => void;
  /** Discards the preview and returns to its capture mode. */
  onSelectAgain: () => void;
  /** Confirms the reference with an optional annotation. */
  onSave: (comment: string) => void;
}

/**
 * Presents a captured or saved PDF reference for review and annotation.
 *
 * @param props - Preview content, prefilled comment, and review actions
 * @returns Modal dialog with preview, comments, and confirmation controls
 */
export const ReviewPdfProofModal: React.FC<ReviewPdfProofModalProps> = ({
  preview,
  initialComment,
  isEditing,
  isSaving,
  onCancel,
  onSelectAgain,
  onSave,
}) => {
  const [comment, setComment] = useState(initialComment);
  const isTextPreview = preview.type === "pdf_text";
  const noun = isTextPreview ? "Annotation" : "Screenshot";
  const showGenericWarning = isGenericComment(comment);

  useEffect(() => setComment(initialComment), [initialComment, preview]);

  return (
    <div
      className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 p-6"
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isSaving) onCancel();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pdf-proof-review-title"
        className="flex w-full max-w-[31rem] flex-col gap-4 rounded-md bg-white p-4 shadow-xl"
      >
        <div className="flex items-center justify-between">
          <h2
            id="pdf-proof-review-title"
            className="text-sm font-bold text-[#0F0F0F]"
          >
            {isEditing ? `Edit ${noun}` : `Review ${noun}`}
          </h2>
          <button
            type="button"
            aria-label="Close annotation review"
            disabled={isSaving}
            onClick={onCancel}
            className="text-lg leading-none text-[#6B6B6B]"
          >
            ×
          </button>
        </div>

        {preview.type === "pdf_text" ? (
          <blockquote className="max-h-56 min-h-[10.5rem] overflow-auto rounded border border-[#D6D6D6] bg-white p-3 text-xs leading-5 text-[#6B6B6B]">
            {preview.text}
          </blockquote>
        ) : (
          <div className="flex min-h-[13rem] items-center justify-center overflow-hidden rounded border border-[#D6D6D6] bg-white">
            {preview.imageUrl ? (
              <img
                src={preview.imageUrl}
                alt="Selected PDF region"
                className="max-h-[13rem] w-full object-contain"
              />
            ) : (
              <span className="text-xs text-[#6B6B6B]">Loading preview...</span>
            )}
          </div>
        )}

        <div className="flex flex-col gap-2">
          <label className="flex flex-col gap-2 text-[11px] font-normal text-[#0F0F0F]">
            Comments
            <input
              type="text"
              value={comment}
              onChange={(event) => setComment(event.target.value)}
              disabled={isSaving}
              placeholder="Enter Comment"
              className="h-9 rounded border border-[#D6D6D6] px-3 text-xs font-normal outline-none focus:border-[#0F0F0F]"
            />
          </label>
          {showGenericWarning && (
            <div
              role="status"
              className="flex items-center gap-2 text-[11px] leading-[16.5px] text-[#C24400]"
            >
              <span className="shrink-0">
                <AlertCircleIcon width="20" height="20" stroke="#C24400" />
              </span>
              <p>{GENERIC_COMMENT_WARNING_MESSAGE}</p>
            </div>
          )}
        </div>

        <div className="flex gap-3">
          <button
            type="button"
            onClick={onSelectAgain}
            disabled={isSaving}
            className="h-9 flex-1 rounded border border-[#0F0F0F] px-4 text-[11px] font-bold disabled:opacity-50"
          >
            {isTextPreview ? "Select Again" : "Retake"}
          </button>
          <button
            type="button"
            onClick={() => onSave(comment)}
            disabled={isSaving}
            className="h-9 flex-1 rounded bg-[#0F0F0F] px-5 text-[11px] font-bold text-white disabled:opacity-50"
          >
            {isSaving
              ? isTextPreview || isEditing
                ? "Saving..."
                : "Uploading..."
              : isTextPreview || isEditing
                ? "Save"
                : "Upload"}
          </button>
        </div>
      </div>
    </div>
  );
};
